import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import { theme } from '../../theme';

const tabs = ['Requests', 'Leave Types', 'Leave Balances'];

function LeaveManagement() {
  const [activeTab, setActiveTab] = useState('Requests');
  const [requests, setRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [typeModal, setTypeModal] = useState(null);
  const [typeForm, setTypeForm] = useState({ name: '', days_per_year: '', carry_forward_limit: 0, applicable_gender: 'all', description: '', is_paid: true, is_active: true, period_type: 'yearly' });
  const [saving, setSaving] = useState(false);
  const [balFilterYear, setBalFilterYear] = useState(new Date().getFullYear());
  const [balFilterEmp, setBalFilterEmp]   = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const r = await hrms.getLeaveRequests(params);
      const d = r.data;
      setRequests(Array.isArray(d) ? d : d.items || []);
    } catch { setRequests([]); }
    finally { setLoading(false); }
  }, [filterStatus]);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const r = await hrms.getLeaveTypes();
      setLeaveTypes(r.data || []);
    } catch { setLeaveTypes([]); }
  }, []);

  const fetchBalances = useCallback(async () => {
    setLoading(true);
    try {
      const r = await hrms.getLeaveBalances({ year: balFilterYear });
      setBalances(r.data || []);
    } catch { setBalances([]); }
    finally { setLoading(false); }
  }, [balFilterYear]);

  useEffect(() => {
    if (activeTab === 'Requests') fetchRequests();
    else if (activeTab === 'Leave Types') fetchLeaveTypes();
    else fetchBalances();
  }, [activeTab, fetchRequests, fetchLeaveTypes, fetchBalances]);

  const handleTypeSave = async () => {
    if (!typeForm.name || !typeForm.days_per_year) {
      alert('Name and Days Per Year are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...typeForm,
        days_per_year: parseFloat(typeForm.days_per_year) || 0,
        carry_forward_limit: parseFloat(typeForm.carry_forward_limit) || 0,
      };
      if (typeModal?.id) {
        await hrms.updateLeaveType(typeModal.id, payload);
      } else {
        await hrms.createLeaveType(payload);
      }
      setTypeModal(null);
      setTypeForm({ name: '', days_per_year: '', carry_forward_limit: 0, applicable_gender: 'all', description: '', is_paid: true, is_active: true, period_type: 'yearly' });
      fetchLeaveTypes();
    } catch (e) {
      alert('Failed to save leave type: ' + (e.response?.data?.detail || e.message));
    }
    finally { setSaving(false); }
  };

  const reqCols = [
    { header: 'Employee', key: 'employee_name', render: (v, row) => v || row.employee?.full_name || '—' },
    { header: 'Leave Type', key: 'leave_type_name', render: (v, row) => v || row.leave_type?.name || '—' },
    { header: 'From', key: 'from_date', render: (v, row) => (v || row.start_date) ? new Date(v || row.start_date).toLocaleDateString() : '—' },
    { header: 'To', key: 'to_date', render: (v, row) => (v || row.end_date) ? new Date(v || row.end_date).toLocaleDateString() : '—' },
    { header: 'Days', key: 'days', render: (v, row) => v || row.total_days || '—' },
    { header: 'Reason', key: 'reason', render: (v) => v ? <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', display: 'block', minWidth: 120 }}>{v}</span> : '—' },
    { header: 'Status', key: 'status', render: (v, row) => <Badge status={v} byName={row.approved_by_name || row.pending_approver_name} /> },
  ];

  const PERIOD_LABEL = { yearly: 'Yearly', monthly: 'Monthly', quarterly: 'Quarterly' };

  const typeCols = [
    { header: 'Name', key: 'name' },
    { header: 'Days / Period', key: 'days_per_year', render: (v, row) => `${v} / ${PERIOD_LABEL[row.period_type] || 'Yearly'}` },
    { header: 'Carry Forward', key: 'carry_forward_limit' },
    { header: 'Gender', key: 'applicable_gender' },
    { header: 'Description', key: 'description', render: (v) => v || '—' },
    { header: 'Paid', key: 'is_paid', render: (v) => v ? '✅ Yes' : '❌ No' },
    { header: 'Active', key: 'is_active', render: (v) => v ? '✅' : '❌' },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outline" onClick={() => {
            setTypeModal(row);
            setTypeForm({ name: row.name, days_per_year: row.days_per_year, carry_forward_limit: row.carry_forward_limit || 0, applicable_gender: row.applicable_gender || 'all', description: row.description || '', is_paid: row.is_paid !== false, is_active: row.is_active !== false, period_type: row.period_type || 'yearly' });
          }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={async () => { if (window.confirm('Delete this leave type?')) { try { await hrms.deleteLeaveType(row.id); fetchLeaveTypes(); } catch { alert('Failed to delete leave type.'); } } }}>Delete</Button>
        </div>
      ),
    },
  ];

  const balCols = [
    { header: 'Employee', key: 'employee_name', render: (v, row) => v || row.employee?.full_name || '—' },
    { header: 'Leave Type', key: 'leave_type_name', render: (v, row) => v || row.leave_type?.name || '—' },
    { header: 'Year', key: 'year' },
    { header: 'Allocated', key: 'allocated', render: (v) => v ?? '—' },
    { header: 'Used', key: 'used', render: (v) => v ?? 0 },
    { header: 'Remaining', key: 'remaining', render: (v) => (
      <span style={{ fontWeight: 600, color: v > 0 ? theme.success : theme.danger }}>{v ?? '—'}</span>
    )},
  ];

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Leave Management</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `2px solid ${theme.border}` }}>
        {tabs.map((t) => (
          <button key={t} onClick={() => setActiveTab(t)}
            style={{ padding: '0.625rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: activeTab === t ? 700 : 400, color: activeTab === t ? theme.primary : theme.textMuted, borderBottom: activeTab === t ? `2px solid ${theme.primary}` : '2px solid transparent', marginBottom: '-2px', fontSize: '0.875rem', transition: 'all 0.15s' }}>
            {t}
          </button>
        ))}
      </div>

      {/* Requests Tab */}
      {activeTab === 'Requests' && (
        <Card padding="0" titleAction={
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '1rem 1.5rem' }}>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        }>
          <Table columns={reqCols} data={requests} loading={loading} emptyMessage="No leave requests found" />
        </Card>
      )}

      {/* Leave Types Tab */}
      {activeTab === 'Leave Types' && (
        <Card title="Leave Types" titleAction={
          <Button size="sm" variant="primary" onClick={() => { setTypeModal({}); setTypeForm({ name: '', days_per_year: '', carry_forward_limit: 0, applicable_gender: 'all', description: '', is_paid: true, is_active: true, period_type: 'yearly' }); }}>
            + Add Leave Type
          </Button>
        }>
          <Table columns={typeCols} data={leaveTypes} loading={loading} emptyMessage="No leave types defined yet." />
        </Card>
      )}

      {/* Leave Balances Tab */}
      {activeTab === 'Leave Balances' && (
        <Card title="Leave Balances" titleAction={
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <input
              type="text"
              value={balFilterEmp}
              onChange={(e) => setBalFilterEmp(e.target.value)}
              placeholder="Search employee..."
              style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', width: '200px' }}
            />
            <select value={balFilterYear} onChange={(e) => setBalFilterYear(parseInt(e.target.value))}
              style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        }>
          <Table
            columns={balCols}
            data={balances.filter(b => !balFilterEmp || (b.employee_name || '').toLowerCase().includes(balFilterEmp.toLowerCase()))}
            loading={loading}
            emptyMessage={`No leave balances for ${balFilterYear}.`}
          />
        </Card>
      )}

      {/* Leave Type Modal */}
      <Modal isOpen={!!typeModal} onClose={() => setTypeModal(null)} title={typeModal?.id ? 'Edit Leave Type' : 'Add Leave Type'}
        footer={<><Button variant="outlineGray" onClick={() => setTypeModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleTypeSave}>Save</Button></>}>
        <label style={lbl}>Name *</label>
        <input style={inp} value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} placeholder="Sick Leave, Casual Leave, Annual Leave..." />
        <label style={lbl}>Period *</label>
        <select style={inp} value={typeForm.period_type} onChange={(e) => setTypeForm({ ...typeForm, period_type: e.target.value })}>
          <option value="yearly">Yearly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
        </select>
        <label style={lbl}>Days Per {typeForm.period_type === 'monthly' ? 'Month' : typeForm.period_type === 'quarterly' ? 'Quarter' : 'Year'} *</label>
        <input style={inp} type="number" min="0" value={typeForm.days_per_year} onChange={(e) => setTypeForm({ ...typeForm, days_per_year: e.target.value })} placeholder={typeForm.period_type === 'monthly' ? '2' : typeForm.period_type === 'quarterly' ? '5' : '12'} />
        <label style={lbl}>Carry Forward Limit (days)</label>
        <input style={inp} type="number" min="0" value={typeForm.carry_forward_limit} onChange={(e) => setTypeForm({ ...typeForm, carry_forward_limit: e.target.value })} placeholder="0" />
        <label style={lbl}>Applicable Gender</label>
        <select style={inp} value={typeForm.applicable_gender} onChange={(e) => setTypeForm({ ...typeForm, applicable_gender: e.target.value })}>
          <option value="all">All</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
        </select>
        <label style={lbl}>Description</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '70px' }} value={typeForm.description} onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })} />
        <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" id="is_paid" checked={!!typeForm.is_paid} onChange={(e) => setTypeForm({ ...typeForm, is_paid: e.target.checked })} />
            <label htmlFor="is_paid" style={{ fontSize: '0.875rem', color: theme.text }}>Paid Leave</label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" id="is_active" checked={!!typeForm.is_active} onChange={(e) => setTypeForm({ ...typeForm, is_active: e.target.checked })} />
            <label htmlFor="is_active" style={{ fontSize: '0.875rem', color: theme.text }}>Active</label>
          </div>
        </div>
      </Modal>

    </div>
  );
}

export default LeaveManagement;
